import { i18n } from 'i18n';
import Message from 'view/shared/message';
import { RootState } from 'modules/store';
import { createSlice } from '@reduxjs/toolkit';
import {
  getBanks,
  getApplicationAndCustomerData,
  postBankRequest,
  postCreateCustomerRequest,
} from './hyperlink.actions';
import {
  BankCategories,
  HyperlinkDataType,
  HyperlinkState,
} from './hyperlink.types';

const initialState: HyperlinkState = {
  data: {
    type: HyperlinkDataType.Customer,
    application: {
      application_id: '',
      account_type: '',
      name: {
        en: '',
        ar: '',
      },
      legal_name: {
        en: '',
        ar: '',
      },
      benefit_statement: {
        en: '',
        ar: '',
      },
      purpose_statement: {
        en: '',
        ar: '',
      },
      logo_url: '',
      sandbox: false,
    },
    customer: {
      customer_id: '',
      account_type: '',
    },
  },
  activeBanks: [],
  disabledBanks: [],
  error: '',
  fetchingTppData: false,
  fetchingBanks: false,
  isSubmitting: false,
  creatingCustomer: false,
  createCustomerError: '',
};

const hyperlinkSlice = createSlice({
  name: 'hyperlink',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getApplicationAndCustomerData.pending, state => {
      state.fetchingTppData = true;
    });
    builder.addCase(
      getApplicationAndCustomerData.fulfilled,
      (state, { payload }) => {
        state.fetchingTppData = false;
        state.data = payload;
      },
    );
    builder.addCase(getApplicationAndCustomerData.rejected, state => {
      state.fetchingTppData = false;
      state.error = i18n('hyperlink.generic_error.subtitle');
    });
    builder.addCase(postCreateCustomerRequest.pending, state => {
      state.creatingCustomer = true;
    });
    builder.addCase(
      postCreateCustomerRequest.fulfilled,
      (state, { payload }) => {
        state.data = {
          type: HyperlinkDataType.Customer,
          application: state.data.application,
          customer: payload,
        };
        state.creatingCustomer = false;
      },
    );
    builder.addCase(postCreateCustomerRequest.rejected, (state, action) => {
      state.creatingCustomer = false;
      state.createCustomerError = action?.payload?.message ?? '';
    });
    builder.addCase(getBanks.pending, state => {
      state.fetchingBanks = true;
    });
    builder.addCase(getBanks.fulfilled, (state, { payload }) => {
      state.fetchingBanks = false;

      // Separate active banks from disabled ones
      const { activeBanks, disabledBanks } = payload.reduce(
        (acc, currentBank) => {
          if (currentBank.is_active) {
            return { ...acc, activeBanks: [...acc.activeBanks, currentBank] };
          }

          return { ...acc, disabledBanks: [...acc.disabledBanks, currentBank] };
        },
        { activeBanks: [], disabledBanks: [] } as BankCategories,
      );

      state.activeBanks = activeBanks;
      state.disabledBanks = disabledBanks;
    });
    builder.addCase(getBanks.rejected, state => {
      state.fetchingBanks = false;
      state.error = i18n('hyperlink.generic_error.subtitle');
    });
    builder.addCase(postBankRequest.pending, state => {
      state.isSubmitting = true;
    });
    builder.addCase(postBankRequest.fulfilled, state => {
      state.isSubmitting = false;
    });
    builder.addCase(postBankRequest.rejected, (state, { payload }) => {
      state.isSubmitting = false;

      if (payload && 'message' in payload) {
        Message.error(payload.message);
      }
    });
  },
});

export default hyperlinkSlice.reducer;
export const selectHyperlink = (state: RootState) => state.root.hyperlink;
